import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { PageHeader, Card, Loading } from "@/components/common";
import { VISIBILITY } from "@/lib/insightsApi";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Building2, Tag, FileText, Eye, Send, Save } from "lucide-react";

const STEPS = ["Department", "Type", "Details", "Visibility", "Review"];

export default function InsightCreate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isApprover = hasMinRole(user?.role, "manager");
  const [step, setStep] = useState(0);
  const [depts, setDepts] = useState(null);
  const [types, setTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    department_id: "", insight_type_id: "", title: "", summary: "", visibility: "", content: {},
  });

  useEffect(() => {
    Promise.all([api.get("/departments"), api.get("/insight-types")])
      .then(([d, t]) => {
        const all = d.data.departments;
        // Only departments where the user has Creator permission are selectable.
        const allowed = isApprover ? all : all.filter((x) => x.department_id === user?.department_id);
        setDepts(allowed);
        setTypes(t.data.insight_types);
        if (allowed.length === 1) setForm((f) => ({ ...f, department_id: allowed[0].department_id }));
      })
      .catch(() => setDepts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selType = types.find((t) => t.type_id === form.insight_type_id);
  const effVisibility = form.visibility || selType?.default_visibility || "staff_internal";

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setContent = (k, v) => setForm((f) => ({ ...f, content: { ...f.content, [k]: v } }));

  const canNext = () => {
    if (step === 0) return !!form.department_id;
    if (step === 1) return !!form.insight_type_id;
    if (step === 2) {
      if (!form.title.trim()) return false;
      const req = (selType?.template_fields || []).filter((tf) => tf.required);
      return req.every((tf) => (form.content[tf.key] || "").toString().trim());
    }
    return true;
  };

  const submit = async (asDraft) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(), summary: form.summary.trim(),
        department_id: form.department_id, insight_type_id: form.insight_type_id,
        visibility: effVisibility, content: form.content,
      };
      const { data } = await api.post("/insights", payload);
      if (!asDraft) {
        // Advanced-engine insights start via the workflow runtime; legacy ones via transition.
        if (data.workflow_engine === "advanced") {
          await api.post(`/insights/${data.insight_id}/workflow/submit`);
        } else {
          await api.post(`/insights/${data.insight_id}/transition`, { target_status: "submitted" });
        }
        toast.success("Insight submitted into workflow");
      } else {
        toast.success("Draft saved");
      }
      navigate(`/insights/${data.insight_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSaving(false);
    }
  };

  if (depts === null) return <Loading label="Preparing creation form…" />;

  if (depts.length === 0) {
    return (
      <div data-testid="create-no-dept">
        <PageHeader title="Create Insight" />
        <Card><p className="text-sm text-slate-600">You are not assigned to a department with Creator permission. Ask an administrator to assign you to a department before authoring Insights.</p></Card>
      </div>
    );
  }

  return (
    <div data-testid="insight-create-page" className="max-w-3xl">
      <PageHeader title="Create Insight" subtitle="Author a new authoritative record and route it into the configured workflow." />

      {/* Stepper */}
      <ol className="flex items-center gap-2 mb-6" aria-label="Progress">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border
              ${i < step ? "bg-brand-teal text-white border-brand-teal"
              : i === step ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-400 border-brand-line"}`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <span className={`text-xs font-medium hidden sm:block ${i === step ? "text-brand-ink" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
          </li>
        ))}
      </ol>

      <Card>
        {step === 0 && (
          <Field icon={Building2} label="Department" hint="Only departments where you may author are shown.">
            <select data-testid="create-department" value={form.department_id}
              onChange={(e) => setField("department_id", e.target.value)}
              className="w-full h-10 rounded-lg border border-brand-line bg-white px-3 text-sm outline-none focus:border-brand-civic">
              <option value="">Select a department…</option>
              {depts.map((d) => <option key={d.department_id} value={d.department_id}>{`${d.name} (${d.code})`}</option>)}
            </select>
          </Field>
        )}

        {step === 1 && (
          <Field icon={Tag} label="Insight Type" hint="Determines the content template and workflow.">
            <div className="grid gap-2">
              {types.filter((t) => t.active !== false).map((t) => (
                <label key={t.type_id} data-testid={`create-type-${t.code}`}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors
                    ${form.insight_type_id === t.type_id ? "border-brand-civic bg-brand-civic-50" : "border-brand-line hover:border-brand-civic/40"}`}>
                  <input type="radio" name="itype" className="mt-1" checked={form.insight_type_id === t.type_id}
                    onChange={() => setField("insight_type_id", t.type_id)} />
                  <div>
                    <div className="font-semibold text-brand-ink text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.description}</div>
                    <div className="text-[11px] text-slate-400 mt-1">Default visibility: {VISIBILITY[t.default_visibility]?.label}</div>
                  </div>
                </label>
              ))}
            </div>
          </Field>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Field icon={FileText} label="Title" required>
              <input data-testid="create-title" value={form.title} onChange={(e) => setField("title", e.target.value)}
                className="w-full h-10 rounded-lg border border-brand-line bg-white px-3 text-sm outline-none focus:border-brand-civic" />
            </Field>
            <Field label="Summary">
              <textarea data-testid="create-summary" value={form.summary} onChange={(e) => setField("summary", e.target.value)} rows={2}
                className="w-full rounded-lg border border-brand-line bg-white p-3 text-sm outline-none focus:border-brand-civic" />
            </Field>
            {(selType?.template_fields || []).map((tf) => (
              <Field key={tf.key} label={tf.label} required={tf.required}>
                {tf.field_type === "textarea"
                  ? <textarea data-testid={`create-field-${tf.key}`} rows={3} value={form.content[tf.key] || ""}
                      onChange={(e) => setContent(tf.key, e.target.value)}
                      className="w-full rounded-lg border border-brand-line bg-white p-3 text-sm outline-none focus:border-brand-civic" />
                  : <input data-testid={`create-field-${tf.key}`} value={form.content[tf.key] || ""}
                      onChange={(e) => setContent(tf.key, e.target.value)}
                      className="w-full h-10 rounded-lg border border-brand-line bg-white px-3 text-sm outline-none focus:border-brand-civic" />}
              </Field>
            ))}
            <p className="text-xs text-slate-400">Attachments can be added on the Insight page after the draft is created.</p>
          </div>
        )}

        {step === 3 && (
          <Field icon={Eye} label="Visibility Review" hint="Who may see this Insight. Enforced by policy on the server.">
            <div className="space-y-2">
              {Object.entries(VISIBILITY).map(([k, v]) => (
                <label key={k} data-testid={`create-visibility-${k}`}
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors
                    ${effVisibility === k ? "border-brand-civic bg-brand-civic-50" : "border-brand-line hover:border-brand-civic/40"}`}>
                  <input type="radio" name="vis" checked={effVisibility === k} onChange={() => setField("visibility", k)} />
                  <span className="text-sm font-semibold text-brand-ink">{v.label}</span>
                  <span className="text-xs text-slate-400 ml-auto">{k}</span>
                </label>
              ))}
              <p className="text-xs text-slate-500 rounded-lg bg-brand-bg border border-brand-line p-3">
                Note: a Public visibility class only makes an Insight <strong>public-eligible</strong>. It is not published
                until an approver sets the publication status after the workflow is finalized.
              </p>
            </div>
          </Field>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="create-review">
            <Row label="Department" value={depts.find((d) => d.department_id === form.department_id)?.name} />
            <Row label="Type" value={selType?.name} />
            <Row label="Title" value={form.title} />
            <Row label="Summary" value={form.summary || "—"} />
            <Row label="Visibility" value={VISIBILITY[effVisibility]?.label} />
            {(selType?.template_fields || []).map((tf) => (
              <Row key={tf.key} label={tf.label} value={form.content[tf.key] || "—"} />
            ))}
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-brand-line">
          <button data-testid="create-back" disabled={step === 0 || saving} onClick={() => setStep((s) => s - 1)}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-brand-bg disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="create-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}
              className="inline-flex items-center gap-1 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex gap-2">
              <button data-testid="create-save-draft" disabled={saving} onClick={() => submit(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-4 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-bg disabled:opacity-40">
                <Save className="h-4 w-4" /> Save Draft
              </button>
              <button data-testid="create-submit" disabled={saving} onClick={() => submit(false)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 disabled:opacity-40">
                <Send className="h-4 w-4" /> Submit
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Field({ icon: Icon, label, hint, required, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink mb-1.5">
        {Icon && <Icon className="h-4 w-4 text-brand-civic" />} {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {hint && <p className="text-xs text-slate-400 mb-2">{hint}</p>}
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-4 text-sm">
      <span className="w-32 shrink-0 font-semibold text-slate-500">{label}</span>
      <span className="text-brand-ink whitespace-pre-wrap">{value}</span>
    </div>
  );
}
